import { apiFetch } from "#lib/api";

export interface IDiscordChannel {
  id: string;
  server_id: string;
  name: string;
  parent_channel_id: string | null;
  topic: string | null;
  theme_color: string | null;
  is_nsfw: boolean;
  position: number;
  icon_emoji: string | null;
  type: number;
  post_count: number;
}

export interface IDiscordServer {
  id: string;
  name: string;
  indexed: string;
  updated: string;
  channels: IDiscordChannel[];
}

export interface IDiscordMessage {
  id: string;
  author: IDiscordMessageAuthor;
  server: string;
  channel: string;
  content: string;
  added: string;
  published: string;
  published_date: Date;
  edited: string | null;
  mentions: IDiscordMention[];
  embeds: IDiscordEmbed[];
  attachments: IDiscordAttachment[];
  seq: number;
  revisions: Omit<IDiscordMessage, "revisions">[];
}

export interface IDiscordMessageAuthor {
  id: string;
  avatar: string;
  username: string;
  public_flags: number;

  discriminator: string;
}

export interface IDiscordMention {
  id: string;
  bot: boolean;
  flags: number;
  avatar: string;
  banner: string | null;
  username: string;
  global_name: string | null;
  accent_color: string | null;
  banner_color: string | null;
  display_name: string | null;
  public_flags: number;
  discriminator: string;
  avatar_decoration: string | null;
}

export interface IDiscordAttachment {
  name: string;
  path: string;
}

export interface IDiscordEmbed {
  title?: string;
  type?: "rich" | "image" | "video" | "gifv" | "article" | "link" | "poll_result";
  description?: string;
  url?: string;
  timestamp?: string;
  color?: number;
  footer?: {
    text: string;
    icon_url?: string;
    proxy_icon_url?: string;
  };
  image?: {
    url: string;
    proxy_url?: string;
    height?: number;
    width?: number;
  };
  thumbnail?: {
    url: string;
    proxy_url?: string;
    height?: number;
    width?: number;
  };
  video?: {
    url?: string;
    proxy_url?: string;
    height?: number;
    width?: number;
  };
  provider?: {
    name?: string;
    url?: string;
  };
  author?: {
    name: string;
    url?: string;
    icon_url?: string;
    proxy_icon_url?: string;
  };
  fields?: {
    name: string;
    value: string;
    inline?: boolean;
  }[];
}

export interface IDiscordMessageGroup {
  author: IDiscordMessageAuthor;
  messages: IDiscordMessage[];
}

export function groupMessages(messages: IDiscordMessage[]): IDiscordMessageGroup[] {
  if (!messages.length) return [];

  let groups: IDiscordMessageGroup[] = [];
  let thisGroup: IDiscordMessage[] = [];

  for (const message of messages) {
    if (!thisGroup.length) {
      thisGroup.push(message);
    } else {
      let lastAuthor = thisGroup[thisGroup.length - 1].author;
      let lastDate = thisGroup[thisGroup.length - 1].published_date;
      let curDate = message.published_date;

      if (lastAuthor.id === message.author.id && Math.abs(curDate.valueOf() - lastDate.valueOf()) < 450_000 /* 7.5 min */) {
        thisGroup.push(message);
      } else {
        groups.push({ author: lastAuthor, messages: thisGroup });
        thisGroup = [message];
      }
    }
  }
  if (thisGroup.length) {
    groups.push({ author: thisGroup[0].author, messages: thisGroup });
  }
  return groups;
}

export async function fetchDiscordServer(serverId: string): Promise<IDiscordServer> {
  return await apiFetch<IDiscordServer>(`/discord/server/${serverId}`, { method: "GET" });
}

export async function fetchDiscordMessages(channelId: string, offset?: number) {
  const path = `/discord/channel/${channelId}`;
  const params = new URLSearchParams();

  if (offset) {
    params.set("o", String(offset));
  }

  let messages = await apiFetch<IDiscordMessage[]>(path, { method: "GET" }, params);
  messages.forEach(message => {
    message.published_date = new Date(message.published);
  });
  return messages;
}

export async function fetchDiscordMessageSeq(channelId: string, messageId: string) {
  return await apiFetch<{ seq: number }>(`/discord/channel/${channelId}/${messageId}`, { method: "GET" });
}
